import * as React from "react";
import { CreditCard, Gift, Bank, Receipt, Star, CheckCircle, Warning } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney, formatSignedMoney } from "./utils";
import type { DayCloseLabels, NonCashTenderCount } from "./types";

const ICONS = {
  card: CreditCard,
  gift_card: Gift,
  transfer: Bank,
  on_account: Receipt,
  loyalty_points: Star,
} as const;

export interface NonCashTenderPanelProps {
  tenders: readonly NonCashTenderCount[];
  onChange: (tenders: readonly NonCashTenderCount[]) => void;
  perTenderToleranceMinor: number;
  labels: DayCloseLabels;
  locale: "tr" | "en";
  className?: string;
}

export function NonCashTenderPanel({
  tenders, onChange, perTenderToleranceMinor, labels, locale, className,
}: NonCashTenderPanelProps) {
  return (
    <ul className={cn("space-y-3", className)}>
      {tenders.map((t) => (
        <TenderRow
          key={t.method}
          tender={t}
          tolerance={perTenderToleranceMinor}
          labels={labels}
          locale={locale}
          onPatch={(patch) =>
            onChange(tenders.map((x) => (x.method === t.method ? { ...x, ...patch } : x)))
          }
        />
      ))}
    </ul>
  );
}

function TenderRow({
  tender, tolerance, labels, locale, onPatch,
}: {
  tender: NonCashTenderCount;
  tolerance: number;
  labels: DayCloseLabels;
  locale: "tr" | "en";
  onPatch: (patch: Partial<NonCashTenderCount>) => void;
}) {
  const [open, setOpen] = React.useState(true);
  const Icon = ICONS[tender.method];
  const variance = tender.countedMinor - tender.expectedMinor;
  const within = Math.abs(variance) <= tolerance;
  const methodLabel = labels.steps.non_cash.methods[tender.method] ?? tender.method;

  return (
    <li className="rounded-biz-card border border-biz-border bg-biz-surface-elevated overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] biz-focus-ring text-left"
      >
        <Icon className="w-6 h-6 text-biz-primary" />
        <span className="flex-1 font-semibold text-biz-foreground">{methodLabel}</span>
        <span className="px-2 py-0.5 rounded-full bg-biz-muted text-xs font-semibold text-biz-foreground-muted">
          {tender.transactionCount} {labels.steps.non_cash.txCount}
        </span>
        <span
          className={cn(
            "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold font-mono-num tabular-nums border",
            within
              ? "bg-biz-success-subtle border-biz-success-border text-biz-success"
              : "bg-biz-warning-subtle border-biz-warning-border text-biz-warning",
          )}
        >
          {within ? <CheckCircle weight="fill" className="w-3 h-3" /> : <Warning weight="fill" className="w-3 h-3" />}
          {formatSignedMoney(variance, labels.currencyPrefix, locale)}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 grid gap-3 md:grid-cols-3">
          <Field label={labels.steps.non_cash.expected}>
            <span className="font-mono-num tabular-nums text-base text-biz-foreground-muted">
              {formatMoney(tender.expectedMinor, labels.currencyPrefix, locale)}
            </span>
          </Field>
          <Field label={labels.steps.non_cash.counted}>
            <CurrencyInput
              valueMinor={tender.countedMinor}
              currencyPrefix={labels.currencyPrefix}
              onChange={(v) => onPatch({ countedMinor: v })}
              ariaLabel={`${methodLabel} ${labels.steps.non_cash.counted}`}
            />
          </Field>
          <Field label={labels.steps.non_cash.reference}>
            <input
              type="text"
              value={tender.referenceSlip ?? ""}
              onChange={(e) => onPatch({ referenceSlip: e.target.value || null })}
              placeholder={labels.steps.non_cash.referencePlaceholder}
              className="h-12 px-3 rounded-biz-btn border border-biz-border bg-biz-bg text-biz-foreground placeholder:text-biz-foreground-subtle biz-focus-ring text-sm"
            />
          </Field>
        </div>
      )}
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted">{label}</span>
      {children}
    </label>
  );
}

function CurrencyInput({
  valueMinor, currencyPrefix, onChange, ariaLabel,
}: {
  valueMinor: number;
  currencyPrefix: string;
  onChange: (v: number) => void;
  ariaLabel: string;
}) {
  const [text, setText] = React.useState(() => (valueMinor / 100).toFixed(2));
  React.useEffect(() => {
    setText((valueMinor / 100).toFixed(2));
  }, [valueMinor]);

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-muted font-mono-num pointer-events-none">
        {currencyPrefix}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        aria-label={ariaLabel}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^\d.,]/g, "").replace(",", ".");
          setText(cleaned);
          const num = Number(cleaned || 0);
          if (!Number.isNaN(num)) onChange(Math.round(num * 100));
        }}
        className="h-12 w-full pl-8 pr-3 rounded-biz-btn border border-biz-border bg-biz-bg text-biz-foreground font-mono-num tabular-nums biz-focus-ring text-base"
      />
    </div>
  );
}
